import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, TrendingUp, Users, DollarSign, BookOpen, Image, Users2, Pencil, CheckSquare } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { TabGeral } from './components/TabGeral';
import { TabLivroCaixa } from './components/TabLivroCaixa';
import { TabPatrocinadores } from './components/TabPatrocinadores';
import { TabInscricoes } from './components/TabInscricoes';
import { TabAnexos } from './components/TabAnexos';
import { TabComissoes } from '@/components/comissoes/TabComissoes';
import { TabChecklists } from './components/TabChecklists';

type TabType = 'geral' | 'financeiro' | 'patrocinadores' | 'inscricoes' | 'anexos' | 'comissoes' | 'checklists';

export function EventoDetailPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('geral');

  const { data: evento, isLoading } = trpc.eventos.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!evento) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Evento não encontrado</p>
        <Button variant="outline" onClick={() => navigate('/eventos')} className="mt-4">
          Voltar para eventos
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'geral' as TabType, label: 'Geral', icon: TrendingUp },
    { id: 'inscricoes' as TabType, label: 'Inscrições', icon: Users, count: evento._count?.inscricoes || 0 },
    { id: 'financeiro' as TabType, label: 'Financeiro', icon: BookOpen },
    { id: 'patrocinadores' as TabType, label: 'Patrocinadores', icon: DollarSign, count: evento._count?.patrocinadores || 0 },
    { id: 'comissoes' as TabType, label: 'Comissões', icon: Users2, count: evento._count?.comissoes || 0 },
    { id: 'checklists' as TabType, label: 'Checklists', icon: CheckSquare, count: evento._count?.checklists || 0 },
    { id: 'anexos' as TabType, label: 'Anexos', icon: Image, count: evento._count?.anexos || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Calendar className="h-4 w-4" />
          <span>Evento</span>
        </div>

        {/* Title Row */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{evento.nome}</h1>
              <div className="flex items-center gap-1.5">
                <span
                  className={`h-2 w-2 rounded-full ${
                    evento.ativo ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                />
                <span
                  className={`text-sm ${
                    evento.ativo ? 'text-green-600' : 'text-gray-500'
                  }`}
                >
                  {evento.ativo ? 'Ativo' : 'Encerrado'}
                </span>
              </div>
            </div>

            {/* Subtitle */}
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              {evento.local && (
                <>
                  <span>{evento.local}</span>
                  <span>•</span>
                </>
              )}
              <span>
                {formatDate(evento.dataInicio)} - {formatDate(evento.dataFim)}
              </span>
            </div>
          </div>

          <Button variant="outline" onClick={() => navigate(`/eventos/${id}/editar`)} disabled={!canEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Editar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white pt-2 pb-0">
        <div className="overflow-x-auto scrollbar-hide rounded-lg border border-gray-200 bg-gray-50 p-1">
          <nav className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium md:gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                    activeTab === tab.id ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'geral' && (
          <TabGeral
            eventoId={id!}
            onNavigateTab={(tab) => setActiveTab(tab as TabType)}
          />
        )}
        {activeTab === 'financeiro' && <TabLivroCaixa eventoId={id!} />}
        {activeTab === 'patrocinadores' && <TabPatrocinadores eventoId={id!} eventoNome={evento.nome} dataInicio={evento.dataInicio} />}
        {activeTab === 'inscricoes' && <TabInscricoes eventoId={id!} valorInscricao={evento.valorInscricao} limiteParticipantes={evento.limiteParticipantes} />}
        {activeTab === 'comissoes' && <TabComissoes eventoId={id!} />}
        {activeTab === 'checklists' && <TabChecklists eventoId={id!} />}
        {activeTab === 'anexos' && <TabAnexos eventoId={id!} />}
      </div>
    </div>
  );
}

export default EventoDetailPage;
